import { api } from "@/lib/api"
import type {
	CheckPaymentStatusResponse,
	CREATE_TRANSACTION_RESPONSE,
	ORDER_CREATION_RESPONSE,
	PAYMENT_META_RESPONSE,
	TransactionStatus,
} from "@/types/form-types"
import { logger } from "@/utils/logger"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

export function useHandlePayment({
	mode,
	applicationID,
	batchID,
	courseID,
	partnerID,
	stage_check_in_time,
}: {
	mode: string
	applicationID: string
	batchID: string
	courseID: string
	partnerID: string
	stage_check_in_time: string
}) {
	// api.interceptors.request.use(apiConfig => {
	// 	apiConfig.headers["mode"] = mode
	// 	apiConfig.headers["X-Partner-ID"] = partnerID
	// 	apiConfig.headers["X-Batch-ID"] = batchID
	// 	return apiConfig
	// })

	const queryClient = useQueryClient()

	useEffect(() => {
		if (!partnerID || !batchID || !mode) return

		const id = api.interceptors.request.use(req => {
			logger.info("useHandlePayment interceptor", { req })
			req.headers["mode"] = window.__DF_MODE
			req.headers["X-Batch-ID"] = batchID
			req.headers["X-Partner-ID"] = partnerID
			return req
		})

		return () => api.interceptors.request.eject(id)
	}, [batchID, partnerID, mode])

	// const stageEntryTimestamp = useRef<number>(Date.now())

	// // Convert timestamp to the desired format
	// const formatTimestamp = (timestamp: number) => {
	// 	const date = new Date(timestamp)
	// 	const isoString = date.toISOString() // Returns in 'YYYY-MM-DDTHH:mm:ss.sssZ'

	// 	// Extract milliseconds and add some fraction (you can adjust this if needed)
	// 	const milliseconds = date.getMilliseconds()
	// 	const fractionalMilliseconds = Math.random().toFixed(5).slice(2) // Add up to 5 decimal places
	// 	const formatted = isoString.replace(
	// 		/\.(\d{3})Z$/,
	// 		`.${milliseconds}${fractionalMilliseconds}Z`,
	// 	)

	// 	return formatted
	// }

	// const formattedTimestamp = formatTimestamp(stageEntryTimestamp.current)
	// to check whether the user already paid ... called when the component loads
	// pending - dev
	const useCheckPaymentStatus = ({
		category,
		semester,
	}: {
		category: string
		semester: number
	}) => {
		const payload = {
			batch_id: batchID,
			course_id: courseID,
			category,
			semester: semester,
		}

		return useQuery({
			queryFn: async () => {
				const { data } = await api.post<CheckPaymentStatusResponse>(
					`/applications/${applicationID}/payment-status`,
					payload,
				)

				return data
			},
			queryKey: [
				"check-payment-status",
				applicationID,
				batchID,
				courseID,
				mode,
				category,
				semester,
			],
			enabled: !!applicationID && !!batchID && !!courseID && !!mode,
		})
	}

	// create-order will be fired on click of pay-now button
	const useCreateOrder = ({
		feeGridRevisionID,
		category,
		plan,
		currency,
		semester,
		mode,
		method,
		items,
		adjustments,
	}: {
		feeGridRevisionID: string
		category: string
		plan: string
		currency: string
		semester: number
		mode: string
		method: string
		items: {
			fee_component_id: string
			component_dimension_id: string
			amount: number
			currency: string
			period_type: string
			period_number: number
		}[]
		adjustments: {
			amount: number
			currency: string
			type: string
			applied_by: string
			applied_at: string
			description: string
		}[]
	}) => {
		const payload = {
			application_id: applicationID,
			batch_id: batchID,
			course_id: courseID,
			fee_grid_revision_id: feeGridRevisionID,
			category,
			semester,
			plan,
			currency,
			mode,
			method,
			items,
			adjustments,
			created_at: stage_check_in_time,
		}

		return useMutation({
			mutationFn: async () => {
				const { data } = await api.post<ORDER_CREATION_RESPONSE>(
					"/orders",
					payload,
				)

				return data
			},
			mutationKey: [
				"create-order",
				applicationID,
				batchID,
				courseID,
				mode,
			],
		})
	}

	const useCreateTransaction = () => {
		return useMutation({
			mutationFn: async ({
				orderID,
				gatewayID,
				paymentMeta,
			}: {
				orderID: string
				gatewayID: string
				paymentMeta: PAYMENT_META_RESPONSE
			}) => {
				const payload = {
					customer_name: paymentMeta?.full_name,
					customer_email: paymentMeta?.email,
					customer_phone: paymentMeta?.phone,
				}
				logger.info(
					"initiating payment gateway from useCreateTransaction",
					payload,
				)
				const { data } = await api.post<CREATE_TRANSACTION_RESPONSE>(
					`/orders/${orderID}/transactions/initiate?gateway=${gatewayID}`,
					payload,
				)
				return data
			},
			mutationKey: ["create-transaction", applicationID],
		})
	}

	// api call (not a hook)
	async function checkTransactionStatus({
		orderID,
		transactionID,
		category,
		semester,
	}: {
		orderID: string
		transactionID: string
		category: string
		semester: number
	}) {
		const data = await api.get<{
			status: TransactionStatus
			paid_at?: string
		}>(`/orders/${orderID}/transactions/${transactionID}/status`)

		if (data.status === 200) {
			if (data.data.status === "SUCCESS") {
				// Invalidate payment status query to refetch updated status
				queryClient.invalidateQueries({
					queryKey: [
						"check-payment-status",
						applicationID,
						batchID,
						courseID,
						mode,
						category,
						semester,
					],
				})
			}
			return data.data
		}
	}

	// Download receipt
	const useHandleDownloadReceipt = () => {
		// try {
		// 	const orderId = paymentInfo?.order_id
		// 	const response = await api.get(
		// 		`/applications/${applicationId}/orders/${orderId}/receipt`,
		// 		{
		// 			responseType: "blob",
		// 		},
		// 	)

		// 	if (response.status !== 200) {
		// 		throw new Error("Failed to download receipt")
		// 	}

		// 	let fileData = response.data
		// 	if (!fileData || fileData.size === 0) {
		// 		// fallback dummy PDF data
		// 		const dummyPdfBytesData = dummyPdfBytes

		// 		fileData = new Blob([dummyPdfBytesData], {
		// 			type: "application/pdf",
		// 		})
		// 	}

		// 	const blob =
		// 		fileData instanceof Blob
		// 			? fileData
		// 			: new Blob([fileData], { type: "application/pdf" })
		// 	const url = window.URL.createObjectURL(blob)

		// 	const a = document.createElement("a")
		// 	a.href = url
		// 	a.download = `receipt_001_${Date.now()}.pdf`
		// 	document.body.appendChild(a)
		// 	a.click()
		// 	a.remove()
		// 	window.URL.revokeObjectURL(url)
		// } catch {
		// 	alert("Failed to download receipt")
		// }

		return useMutation({
			mutationFn: async ({ orderId }: { orderId: string }) => {
				const { data } = await api.get(
					`/applications/${applicationID}/orders/${orderId}/receipt`,
					{
						responseType: "blob",
					},
				)

				return data
			},
			mutationKey: ["download-receipt"],
		})
	}

	return {
		useCheckPaymentStatus,
		useCreateOrder,
		useCreateTransaction,
		checkTransactionStatus,
		useHandleDownloadReceipt,
	}
}
